import { createApp } from 'vue'
import './style.css'
import './assets/styles.css'

// Test: Content hash should change when this file is modified
import App from './App.vue'
import i18n from './i18n'
import { loadUnbluApi } from './utils/loadUnbluApi'
import { getCurrentEnvironment } from './config/environments'
import './utils/agentUtils' // Import agent utilities globally

// Initialize client logger FIRST (before any console filtering)
// This ensures all console.log calls are sent to Express server for Google Cloud Logging
import { initClientLogger, logWithSuffix } from './utils/clientLogger'

async function bootstrapApp() {
  initClientLogger()

  // Load Unblu API dynamically based on environment
  loadUnbluApi()
    .then(() => {
      const app = createApp(App)
      app.use(i18n)
      app.mount('#app')

      const env = getCurrentEnvironment()
      console.debug(`Running in ${env.name} environment`)
      // Hash test: Include build timestamp to verify new build
      // const buildTimestamp = document.querySelector('meta[name="build-timestamp"]')?.getAttribute('content') || 'unknown'
      // console.log(`Build hash test - build timestamp: ${buildTimestamp}`)
    })
    .catch((error) => {
      logWithSuffix('error', 'APP-INIT-FAILED', 'Failed to initialize application:', error)
    })
}

bootstrapApp()

try {
  const baseUrl = (window as any).VITE_BASE_URL || 'unknown'
  const appMode = (window as any).VITE_APP_MODE || 'unknown'
  console.debug('VITE_BASE_URL:', baseUrl)
  console.debug('VITE_APP_MODE:', appMode)
} catch (e) {
  // Silently fail
}

// Windows-optimized viewport height calculation with KIOSK support
function adjustLayoutForViewport() {
  // Detect KIOSK mode (fullscreen or presentation mode)
  const isKioskMode = window.innerHeight === window.screen.height || 
                     document.fullscreenElement !== null ||
                     window.matchMedia('(display-mode: fullscreen)').matches;
  
  // Detect Windows and high DPI displays
  const isWindows = navigator.platform.toUpperCase().indexOf('WIN') >= 0;
  const isHighDPI = window.devicePixelRatio >= 1.25;
  
  // Only subtract taskbar height for Windows desktop scenarios
  const taskbarHeight = isWindows && !isKioskMode ? 50 : 0;
  const adjustedVh = (window.innerHeight - taskbarHeight) * 0.01;
  document.documentElement.style.setProperty('--vh', `${adjustedVh}px`);
  
  // Add platform-specific classes for CSS targeting
  if (isWindows) {
    document.documentElement.classList.add('windows');
    if (isHighDPI) {
      document.documentElement.classList.add('high-dpi');
    }
  } else {
    document.documentElement.classList.remove('windows', 'high-dpi');
  }
  
  // Add KIOSK mode class
  if (isKioskMode) {
    document.documentElement.classList.add('kiosk-mode');
  } else {
    document.documentElement.classList.remove('kiosk-mode');
  }
  
  console.debug('Viewport adjusted:', {
    innerHeight: window.innerHeight,
    screenHeight: window.screen.height,
    adjustedVh,
    isKioskMode,
    isWindows,
    isHighDPI,
    devicePixelRatio: window.devicePixelRatio
  });
}

// Call on resize and orientation change
window.addEventListener('resize', adjustLayoutForViewport);
window.addEventListener('orientationchange', adjustLayoutForViewport);

// Listen for fullscreen changes (KIOSK mode)
document.addEventListener('fullscreenchange', adjustLayoutForViewport);
document.addEventListener('webkitfullscreenchange', adjustLayoutForViewport);
document.addEventListener('mozfullscreenchange', adjustLayoutForViewport);
document.addEventListener('MSFullscreenChange', adjustLayoutForViewport);

// Initial call
adjustLayoutForViewport();